import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { motion } from "motion/react";
import React from "react";
import { useColorModeContext } from "../color-mode";
import { useTranslation } from "../localization";
import { Portal } from "../portal";
import { Spinner } from "../spinner";

/** Props Interface */
interface LazyFallbackProps extends FallbackProps {}

export function LazyFallback({ variant }: LazyFallbackProps) {
	/** Render */
	return (
		<React.Fragment>
			{variant === "inline" && <Fallback variant="inline" />}
			{variant === "portal" && (
				<Portal>
					<Fallback variant="portal" />
				</Portal>
			)}
		</React.Fragment>
	);
}

/** Props Interface */
type FallbackProps = Required<VariantProps<typeof fallback>>;

function Fallback({ variant }: FallbackProps) {
	const { colorMode } = useColorModeContext();
	const { t } = useTranslation();
	const classes = fallback({ variant });

	return (
		<motion.div
			className={clsx(classes)}
			initial={{ opacity: 0, scale: 0.5 }}
			animate={{ opacity: 1, scale: 1 }}
			exit={{ opacity: 0, scale: 0.5 }}
		>
			<Spinner color={colorMode === "dark" ? "white" : "black"} />
			<p className="text-sm">{t("general.loading")}</p>
		</motion.div>
	);
}

const fallback = cva(["flex-row flex-center gap-4"], {
	variants: {
		variant: {
			portal: ["bottom-4 left-4 fixed bg-white dark:bg-black shadow-md p-2 rounded-lg"],
			inline: [],
		},
	},
});
